// React / Next
import { Link } from '@tanstack/react-router';
import { motion, Variants } from 'framer-motion';

// Components
import { Typography } from '@archieai/ui';

// Utils
import { cn } from '../../utils/cn';

// Types
type MagicCardVariant = 'orange' | 'lightBlue' | 'darkBlue' | 'teal';

type MagicCardProps = {
  label: string;
  title: string;
  description: string;
  variant?: MagicCardVariant;
  href?: string;
  className?: string;
  Graphic: React.ComponentType;
};

// Variant styling configuration
const variantConfig: Record<
  MagicCardVariant,
  {
    bgColor: string;
    bgColorHex: string;
    titleColor: string;
    labelColor: string;
    hoverTitleColor: string;
    hoverShadow: string;
  }
> = {
  orange: {
    bgColor: 'bg-orange-200',
    bgColorHex: '#FFF2E2',
    titleColor: 'text-blue-1000',
    labelColor: 'text-orange-800',
    hoverTitleColor: 'text-blue-1000',
    hoverShadow: '0 12px 48px 0px rgba(255, 180, 100, 0.5)',
  },
  lightBlue: {
    bgColor: 'bg-blue-200',
    bgColorHex: '#D6F4FF',
    titleColor: 'text-blue-1000',
    labelColor: 'text-orange-800',
    hoverTitleColor: 'text-blue-1000',
    hoverShadow: '0 12px 48px 0px rgba(100, 200, 255, 0.5)',
  },
  darkBlue: {
    bgColor: 'bg-blue-1000',
    bgColorHex: '#022E40',
    titleColor: 'text-orange-200',
    labelColor: 'text-orange-800',
    hoverTitleColor: 'text-blue-1000',
    hoverShadow: '0 12px 48px 0px rgba(2, 46, 64, 0.6)',
  },
  teal: {
    bgColor: 'bg-blue-800',
    bgColorHex: '#15B1CD',
    titleColor: 'text-orange-200',
    labelColor: 'text-orange-800',
    hoverTitleColor: 'text-blue-1000',
    hoverShadow: '0 12px 48px 0px rgba(21, 177, 205, 0.6)',
  },
};

// Animation variants
const containerVariants: Variants = {
  initial: { transition: { staggerChildren: 0.02 } },
  rest: { transition: { staggerChildren: 0.02 } },
  hover: { transition: { staggerChildren: 0.02 } },
};

const wordVariants: Variants = {
  initial: { y: 0, opacity: 1 },
  rest: {
    y: 0,
    opacity: 1,
    transition: {
      type: 'spring',
      stiffness: 70,
      damping: 20,
      delay: 0.3,
    },
  },
  hover: {
    y: -25,
    opacity: 0,
    transition: {
      duration: 0.2,
      ease: 'easeIn',
    },
  },
};

const invertedWordVariants: Variants = {
  initial: { y: 20, opacity: 0 },
  rest: {
    y: 20,
    opacity: 0,
    transition: {
      duration: 0.15,
      ease: 'easeIn',
    },
  },
  hover: {
    y: 0,
    opacity: 1,
    transition: {
      type: 'spring',
      stiffness: 50,
      damping: 15,
      delay: 0.3,
    },
  },
};

const labelVariants: Variants = {
  initial: { opacity: 0, x: -20 },
  rest: { opacity: 0, x: -20, transition: { duration: 0.2 } },
  hover: { opacity: 1, x: 0, transition: { duration: 0.4, delay: 0.3 } },
};

const ctaVariants: Variants = {
  initial: { opacity: 0, y: 10 },
  rest: { opacity: 0, y: 10, transition: { duration: 0.2 } },
  hover: { opacity: 1, y: 0, transition: { delay: 0.5, duration: 0.3 } },
};

// SplitText component for word-by-word animation
type SplitTextProps = {
  children: string;
  className?: string;
  variants: Variants;
};

const SplitText = ({ children, className, variants }: SplitTextProps) => {
  const words = children.split(' ');

  return (
    <span className={className}>
      {words.map((word, i) => (
        <span key={i}>
          <span className="inline-block overflow-hidden align-top">
            <motion.span variants={variants} className="inline-block">
              {word}
            </motion.span>
          </span>
          {i < words.length - 1 && ' '}
        </span>
      ))}
    </span>
  );
};

// Arrow Icon component
const ArrowIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

export const MagicCard = ({
  label,
  title,
  description,
  variant = 'orange',
  href,
  className,
  Graphic,
}: MagicCardProps) => {
  const config = variantConfig[variant];

  const bgVariants: Variants = {
    initial: {
      backgroundColor: config.bgColorHex,
      boxShadow: '0 0 0 0px transparent, 0 0 0 0px transparent',
    },
    rest: {
      backgroundColor: config.bgColorHex,
      boxShadow: '0 0 0 0px transparent, 0 0 0 0px transparent',
    },
    hover: {
      backgroundColor: '#ffffff',
      boxShadow: `inset 0 0 0 1px #E0E0E0, ${config.hoverShadow}`,
    },
  };

  const cardContent = (
    <>
      {/* Background layer with color transition */}
      <motion.div
        className="absolute inset-0 z-0 rounded-[var(--radius-12)]"
        variants={bgVariants}
        transition={{ duration: 0.5, ease: 'easeInOut' }}
      />

      {/* Hover state content (description + CTA) */}
      <div className="absolute inset-0 z-10 p-[var(--spacing-24)] flex flex-col justify-between pointer-events-none">
        <div className="overflow-hidden relative z-20">
          {/* Label */}
          <motion.div variants={labelVariants}>
            <Typography
              variant="column-header"
              className={cn('mb-[var(--spacing-16)]', config.labelColor)}
            >
              {label}
            </Typography>
          </motion.div>

          {/* Description (appears on hover) */}
          <Typography
            as="p"
            variant="h3"
            className={cn('max-w-[95%]', config.hoverTitleColor)}
          >
            <SplitText variants={invertedWordVariants}>{description}</SplitText>
          </Typography>
        </div>

        {/* Explore CTA */}
        <motion.div
          className={cn(
            'flex items-center font-semibold pointer-events-auto w-fit',
            config.hoverTitleColor
          )}
          variants={ctaVariants}
        >
          <Typography variant="small" className="mr-[var(--spacing-12)]">
            Explore
          </Typography>
          <ArrowIcon />
        </motion.div>
      </div>

      {/* Rest state content (large title) */}
      <div className="absolute inset-0 z-20 p-[var(--spacing-24)] flex flex-col justify-end pointer-events-none">
        <div className="mb-[var(--spacing-8)]">
          <Typography
            as="h3"
            variant="h2"
            className={cn('max-w-[60%]', config.titleColor)}
          >
            <SplitText variants={wordVariants}>{title}</SplitText>
          </Typography>
        </div>
      </div>

      {/* SVG Graphic */}
      <div className="absolute right-0 bottom-0 z-10 pointer-events-none h-[70%] w-auto origin-bottom-right">
        <Graphic />
      </div>
    </>
  );

  const baseClasses = cn(
    'relative w-full max-w-[496px] aspect-[496/365] rounded-[var(--radius-12)] overflow-hidden cursor-pointer flex-shrink-0',
    className
  );

  const MotionLink = motion.create(Link);

  if (href) {
    return (
      <MotionLink
        to={href}
        className={baseClasses}
        initial="initial"
        animate="rest"
        whileHover="hover"
        whileTap="hover"
        variants={containerVariants}
        aria-label={`Learn more about Archie for ${title}`}
      >
        {cardContent}
      </MotionLink>
    );
  }

  return (
    <motion.article
      className={baseClasses}
      initial="initial"
      animate="rest"
      whileHover="hover"
      whileTap="hover"
      variants={containerVariants}
    >
      {cardContent}
    </motion.article>
  );
};

